'use client';

import { useCallback, useEffect, useState } from 'react';
import styles from './SectorSpecificsCarousel.module.scss';
import { useAOS } from '@/src/lib/hooks/useAOS';

const IconChevronLeft = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconChevronRight = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export interface SpecificityItem {
  number: string;
  title: string;
  description: string;
}

export interface SectorSpecificsCarouselProps {
  eyebrow?: string;
  title?: string;
  items?: SpecificityItem[];
}

export function SectorSpecificsCarousel({
  eyebrow = 'Spécificités du secteur',
  title = 'Ce qui rend ce secteur exigeant',
  items = [],
}: SectorSpecificsCarouselProps) {
  useAOS();

  if (items.length === 0) {
    return null;
  }

  const [activeIndex, setActiveIndex] = useState(0);
  const [detailKey, setDetailKey] = useState(0);

  const goTo = useCallback(
    (index: number) => {
      if (index === activeIndex) return;
      setActiveIndex(index);
    },
    [activeIndex]
  );

  const handlePrev = useCallback(() => {
    goTo((activeIndex - 1 + items.length) % items.length);
  }, [activeIndex, goTo, items.length]);

  const handleNext = useCallback(() => {
    goTo((activeIndex + 1) % items.length);
  }, [activeIndex, goTo, items.length]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handlePrev, handleNext]);

  const activeItem = items[activeIndex];

  useEffect(() => {
    setDetailKey((prev) => prev + 1);
  }, [activeIndex]);

  return (
    <section className={styles.root} aria-labelledby="specifics-title">
      <div className={styles.inner}>
        <div className={styles.header} data-aos="fade-up" data-aos-duration="700">
          {eyebrow && <p className={styles.eyebrow}>{eyebrow}</p>}
          {title && (
            <h2 id="specifics-title" className={styles.title}>
              {title}
            </h2>
          )}
        </div>

        <div
          className={styles.stage}
          data-aos="fade-up"
          data-aos-duration="900"
          data-aos-delay="150"
        >
          <button
            type="button"
            className={`${styles.navButton} ${styles.navButtonLeft}`}
            onClick={handlePrev}
            aria-label="Spécificité précédente"
          >
            <IconChevronLeft />
          </button>

          <div className={styles.viewport} aria-live="polite" aria-atomic="true">
            <div className={styles.cardTrack}>
              {items.map((item, index) => {
                const isActive = index === activeIndex;
                const isPrev = index === (activeIndex - 1 + items.length) % items.length;
                const isNext = index === (activeIndex + 1) % items.length;
                let position = styles.cardHidden;
                if (isActive) position = styles.cardActive;
                else if (isPrev) position = styles.cardPrev;
                else if (isNext) position = styles.cardNext;

                return (
                  <div
                    key={`${item.title}-${index}`}
                    className={`${styles.card} ${styles[`gradient${index % 5}`]} ${position}`}
                    aria-hidden={!isActive}
                  >
                    <span className={styles.number} aria-hidden="true">
                      {item.number}
                    </span>
                    <div className={styles.cardBody}>
                      <h3 className={styles.cardTitle}>{item.title}</h3>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.detailPanel}>
              <div className={styles.detailInner}>
                <span className={styles.detailNumber}>{activeItem.number}</span>
                <p key={detailKey} className={styles.detailText}>
                  {activeItem.description}
                </p>
              </div>
            </div>
          </div>

          <button
            type="button"
            className={`${styles.navButton} ${styles.navButtonRight}`}
            onClick={handleNext}
            aria-label="Spécificité suivante"
          >
            <IconChevronRight />
          </button>
        </div>

        <div className={styles.footerNav}>
          <div className={styles.progressBar} aria-hidden="true">
            <div
              className={styles.progressFill}
              style={{ width: `${((activeIndex + 1) / items.length) * 100}%` }}
            />
          </div>

          <div className={styles.dots} role="tablist" aria-label="Choisir une spécificité">
            {items.map((item, index) => (
              <button
                key={`dot-${item.title}-${index}`}
                type="button"
                role="tab"
                aria-selected={index === activeIndex}
                aria-controls={`specificity-panel-${index}`}
                id={`specificity-tab-${index}`}
                className={`${styles.dot} ${index === activeIndex ? styles.dotActive : ''}`}
                onClick={() => goTo(index)}
                aria-label={`${item.number} — ${item.title}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
